/** @jsxImportSource @emotion/react */
import locale from './locale';
import { LOCALE_KEY } from './const';
import { registerLocale } from '@/shared/helpers/language';
import { useTranslation } from 'react-i18next';
import { useState, useEffect, useMemo, useRef } from 'react';

import { Popover, Badge } from 'antd';
import { BellFilled } from '@ant-design/icons';

import NotificationContent from './NotificationContent';
import useCurrentMemberNotification from './usecase/useCurrentMemberNotification';
import { getMemberId } from '@/shared/helpers/userCred';
import EventSourcePolyfill from 'eventsource-polyfill';

import styles from './styles';

const GlobalNotification = () => {
    const { i18n } = useTranslation(LOCALE_KEY);

    registerLocale(i18n, LOCALE_KEY, locale);

    const { rawData, data, refetch } = useCurrentMemberNotification();
    const [deletedNotificationIds, setDeletedNotificationIds] = useState<number[]>([]);
    const [ackedNotificationIds, setAckedNotificationIds] = useState<number[]>([]);
    const [isMobile, setIsMobile] = useState(false);
    const notifEventRef = useRef<EventSourcePolyfill | null>(null);
    const refetchRef = useRef(refetch);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 699);
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    /* Update refetch ref when refetch changes */
    useEffect(() => {
        refetchRef.current = refetch;
    }, [refetch]);

    /* Setup EventSource for real-time notifications */
    useEffect(() => {
        const memberId = getMemberId();
        if (!memberId) {
            console.log('[GlobalNotification] No memberId, skipping EventSource setup');
            return;
        }

        /* Subscribe to all professional service notification topics.
           survei_request_client = My Request Survey | survei_request_professional = Request Survey Order */
        const userNotificationSubs = [
            'from_user.chat_message.user_[USER_ID].*',
            'from_admin.broadcast',
            'from_system.wallet_activity.user_[USER_ID].*',
            'from_system.mark_as_favorite.user_[USER_ID].*',
            'from_system.favorited_item_updated.user_[USER_ID].*',
            'from_system.solution_status_updated.user_[USER_ID].*',
            'from_system.forum_activity.user_[USER_ID].*',
            'from_system.survei_request_client.user_[USER_ID].*.*',
            'from_system.survei_request_professional.user_[USER_ID].*.*',
            'from_system.direct_order_client.user_[USER_ID].*',
            'from_system.direct_order_professional.user_[USER_ID].*',
            'from_system.proposal_order_client.user_[USER_ID].*',
            'from_system.proposal_order_professional.user_[USER_ID].*'
        ];

        const handlingRealtimeEvent = (event: Event) => {
            console.log('[GlobalNotification] Signal event received, scheduling refetch');
            
            /* Clear any existing timeout */
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }

            /* Debounce refetch with longer delay to ensure notification is saved in database */
            /* Use 3 seconds delay to give backend time to save the notification */
            timeoutRef.current = setTimeout(() => {
                console.log('[GlobalNotification] Executing refetch (first attempt after 3s)');
                if (refetchRef.current) {
                    refetchRef.current();
                    
                    /* Second refetch after 2 more seconds as backup */
                    setTimeout(() => {
                        if (refetchRef.current) {
                            console.log('[GlobalNotification] Executing refetch (second attempt after 5s total)');
                            refetchRef.current();
                        }
                    }, 2000);
                    
                    /* Third refetch after 3 more seconds as final backup */
                    setTimeout(() => {
                        if (refetchRef.current) {
                            console.log('[GlobalNotification] Executing refetch (third attempt after 8s total)');
                            refetchRef.current();
                        }
                    }, 5000);
                } else {
                    console.warn('[GlobalNotification] refetchRef.current is null');
                }
            }, 3000);
        };

        const interpolatedUserNotificationSubs = userNotificationSubs.map(sub =>
            sub.replaceAll('[USER_ID]', `${memberId}`)
        );

        const topicsStr = interpolatedUserNotificationSubs.map(sub => `topics[]=${sub}`).join('&');

        if (!notifEventRef.current) {
            const token = localStorage.getItem('accessToken');
            if (!token) {
                console.log('[GlobalNotification] No token, skipping EventSource setup');
                return;
            }

            try {
                const url = `${process.env.REACT_APP_API_URL}/api/v2/notifications/listen?${topicsStr}`;
                console.log('[GlobalNotification] Creating EventSource:', url);

                const xhrHeaders = {
                    Authorization: `Bearer ${token}`,
                    Accept: 'text/event-stream',
                    'Cache-Control': 'no-cache',
                    'X-Requested-With': 'XMLHttpRequest',
                };

                const eventSource = new EventSourcePolyfill(url, {
                    xhrHeaders: xhrHeaders,
                    withCredentials: false,
                });

                eventSource.addEventListener('ping', () => {
                    console.log('[GlobalNotification] EventSource ping received');
                });

                eventSource.addEventListener('signal', handlingRealtimeEvent);

                eventSource.addEventListener('open', () => {
                    console.log('[GlobalNotification] EventSource opened successfully');
                });

                eventSource.addEventListener('error', (error: any) => {
                    const errorMessage = error?.message || error?.toString() || '';
                    const readyState = (error.target as EventSourcePolyfill)?.readyState;
                    
                    console.error('[GlobalNotification] EventSource error:', {
                        errorMessage,
                        readyState,
                        error
                    });

                    /* Ignore connection reset errors (these are normal) */
                    if (
                        errorMessage.includes('ECONNRESET') ||
                        errorMessage.includes('socket hang up') ||
                        errorMessage.includes('Connection closed') ||
                        errorMessage.includes('read ECONNRESET')
                    ) {
                        return;
                    }

                    /* If EventSource is closed, try to reconnect */
                    if (readyState === EventSource.CLOSED) {
                        console.log('[GlobalNotification] EventSource closed, will reconnect on next effect');
                        notifEventRef.current = null;
                    }
                });

                notifEventRef.current = eventSource;
                console.log('[GlobalNotification] EventSource created and stored in ref');
            } catch (error) {
                console.error('[GlobalNotification] Error creating EventSource:', error);
            }
        } else {
            console.log('[GlobalNotification] EventSource already exists, skipping creation');
        }

        return () => {
            /* Clear timeout if exists */
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }

            if (notifEventRef.current) {
                console.log('[GlobalNotification] Cleaning up EventSource');
                notifEventRef.current.removeEventListener('ping', () => {});
                notifEventRef.current.removeEventListener('signal', handlingRealtimeEvent);
                notifEventRef.current.close();
                notifEventRef.current = null;
            }
        };
    }, []);

    const handleNotificationDeleted = (deletedNotificationId: number) => {
        setDeletedNotificationIds(prev => [...prev, deletedNotificationId]);
    };

    const handleNotificationAcked = (notificationId: number) => {
        setAckedNotificationIds(prev =>
            prev.includes(notificationId) ? prev : [...prev, notificationId]
        );
    };

    const filteredData = useMemo(() => {
        if (!data) {
            return data;
        }

        const updatedAggregatedData = { ...data.aggregatedData };

        data.topics.forEach(topic => {
            updatedAggregatedData[topic] = updatedAggregatedData[topic]
                .filter((notif: any) => !deletedNotificationIds.includes(notif.id))
                .map((notif: any) => ({
                    ...notif,
                    acked: notif.acked || ackedNotificationIds.includes(notif.id),
                }));
        });

        const updatedTopics = data.topics.filter(topic => updatedAggregatedData[topic].length > 0);

        return {
            topics: updatedTopics,
            aggregatedData: updatedAggregatedData,
        };
    }, [data, deletedNotificationIds, ackedNotificationIds]);

    const filteredRawData = useMemo(() => {
        if (!rawData) {
            return rawData;
        }

        return rawData.filter(n => !deletedNotificationIds.includes(n.id));
    }, [rawData, deletedNotificationIds]);

    const nonAckedNotifs = filteredRawData?.filter(
        n => n.acked === false && !ackedNotificationIds.includes(n.id)
    );
    const notificationCount = nonAckedNotifs?.length || 0;

    /* Log notification count for debugging */
    useEffect(() => {
        console.log('[GlobalNotification] Notification count updated:', {
            totalRawData: rawData?.length || 0,
            filteredRawData: filteredRawData?.length || 0,
            nonAckedCount: notificationCount,
            deletedIds: deletedNotificationIds.length,
        });
    }, [rawData, filteredRawData, notificationCount, deletedNotificationIds]);

    const handleAllNotificationsDeleted = () => {
        setDeletedNotificationIds([]);

        if (rawData) {
            const allIds = rawData.map(notif => notif.id).filter(id => id !== undefined);
            setDeletedNotificationIds(allIds);
        }
    };

    return (
        <div css={styles.base}>
            <Popover
                placement={isMobile ? 'bottomLeft' : 'topLeft'}
                trigger="click"
                getPopupContainer={trigger => trigger.parentElement || document.body}
                content={
                    <NotificationContent
                        userNotifications={filteredData}
                        onNotificationDeleted={handleNotificationDeleted}
                        onAllNotificationsDeleted={handleAllNotificationsDeleted}
                        onNotificationAcked={handleNotificationAcked}
                        onRefetch={refetch}
                    />
                }
                overlayClassName="global-notification-overwrite"
            >
                <div css={styles.notifBell}>
                    <Badge count={notificationCount}>
                        <BellFilled style={{ fontSize: '18px', color: '#000' }} />
                    </Badge>
                </div>
            </Popover>
        </div>
    );
};

export default GlobalNotification;
